"use client"

import { createContext, useContext, useState } from "react"
import { Toaster } from "@/components/ui/sonner"

type ToastPosition = "top-left" | "top-center" | "top-right" | "bottom-left" | "bottom-center" | "bottom-right"
type ToastTheme = "light" | "dark" | "system"

type ToastConfig = {
  position: ToastPosition
  duration: number
  theme: ToastTheme
  expand: boolean
  visibleToasts: number
}

type ToastContextType = ToastConfig & {
  setConfig: (config: Partial<ToastConfig>) => void
}

const ToastContext = createContext<ToastContextType | undefined>(undefined)

export function useToastConfig() {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error("useToastConfig must be used within ToasterProvider")
  }
  return context
}

type ToasterProviderProps = {
  initialConfig?: Partial<ToastConfig>
  children: React.ReactNode
}

export function ToasterProvider({ 
  initialConfig = {}, 
  children 
}: ToasterProviderProps) {
  const [config, setConfigState] = useState<ToastConfig>({
    position: initialConfig.position || "top-right",
    duration: initialConfig.duration || 4000,
    theme: initialConfig.theme || "system",
    expand: initialConfig.expand || false,
    visibleToasts: initialConfig.visibleToasts || 3
  })

  const setConfig = (newConfig: Partial<ToastConfig>) => {
    setConfigState(prev => ({ ...prev, ...newConfig }))
  }

  return (
    <ToastContext.Provider value={{ ...config, setConfig }}>
      {children}
      <Toaster 
        position={config.position}
        duration={config.duration}
        theme={config.theme}
        expand={config.expand}
        visibleToasts={config.visibleToasts}
        richColors 
        closeButton 
      />
    </ToastContext.Provider>
  )
}
